<template>
  <!-- Cabeçalho da Página -->
  <div class="d-flex justify-end mb-6">
    <v-btn color="primary" prepend-icon="mdi-plus" @click="openDialog()">
      Novo Usuário
    </v-btn>
  </div>

  <!-- Filtros e Busca -->
  <v-card class="mb-6" flat border>
    <v-card-text class="pa-4">
      <div class="d-flex align-center mb-4">
        <v-icon size="22" class="me-3">mdi-filter-outline</v-icon>
        <h2 class="text-subtitle-1 font-weight-medium">Filtros</h2>
      </div>
      <v-row align="center">
        <v-col cols="12" md="4">
          <v-text-field v-model="search" prepend-inner-icon="mdi-magnify" label="Buscar usuários..." variant="outlined"
            density="compact" hide-details clearable />
        </v-col>

        <v-col cols="12" md="3">
          <v-select v-model="statusFilter" :items="statusOptions" label="Status" variant="outlined" density="compact"
            hide-details clearable />
        </v-col>

        <v-col cols="12" md="3">
          <v-select v-model="roleFilter" :items="roleOptions" label="Perfil" variant="outlined" density="compact"
            hide-details clearable />
        </v-col>

        <v-col cols="12" md="2">
          <v-btn variant="outlined" block @click="clearFilters">
            Limpar
          </v-btn>
        </v-col>
      </v-row>
    </v-card-text>
  </v-card>

  <!-- Tabela de Usuários -->
  <v-card flat border>
    <v-data-table :headers="headers" :items="filteredUsers" :loading="loading" :search="search" class="elevation-0"
      item-value="id" show-select v-model="selectedUsers">
      <!-- Slot para Avatar -->
      <template #item.avatar="{ item }">
        <v-avatar size="40" class="my-2">
          <v-img v-if="item.avatar" :src="item.avatar" :alt="item.nome" />
          <v-icon v-else>mdi-account</v-icon>
        </v-avatar>
      </template>

      <!-- Slot para Nome e Email -->
      <template #item.nome="{ item }">
        <div>
          <div class="font-weight-medium">{{ item.nome }}</div>
          <div class="text-caption text-medium-emphasis">{{ item.email }}</div>
        </div>
      </template>

      <!-- Slot para Status -->
      <template #item.ativo="{ item }">
        <v-chip :color="item.ativo === '1' ? 'success' : 'error'" size="small" variant="tonal">
          {{ item.ativo === '1' ? 'Ativo' : 'Inativo' }}
        </v-chip>
      </template>

      <!-- Slot para Perfil -->
      <template #item.administrador="{ item }">
        <v-chip :color="item.administrador === '1' ? 'primary' : 'default'" size="small" variant="tonal">
          {{ item.administrador === '1' ? 'Administrador' : 'Usuário' }}
        </v-chip>
      </template>

      <!-- Slot para Último Acesso -->
      <template #item.ultimo_acesso="{ item }">
        <div v-if="item.ultimo_acesso">
          <div class="text-body-2">{{ (item.ultimo_acesso) }}</div>
        </div>
        <span v-else class="text-medium-emphasis">Nunca</span>
      </template>

      <!-- Slot para Ações -->
      <template #item.actions="{ item }">
        <div class="d-flex gap-1">
          <v-btn icon="mdi-pencil" size="small" variant="text" color="primary" @click="openDialog(item)">
            <v-icon>mdi-pencil</v-icon>
            <v-tooltip activator="parent" location="top">Editar</v-tooltip>
          </v-btn>

          <v-btn icon="mdi-delete" size="small" variant="text" color="error" @click="confirmDelete(item)">
            <v-icon>mdi-delete</v-icon>
            <v-tooltip activator="parent" location="top">Excluir</v-tooltip>
          </v-btn>
        </div>
      </template>

      <!-- Slot para estado vazio -->
      <template #no-data>
        <div class="text-center pa-8">
          <v-icon size="64" color="grey-lighten-2" class="mb-4">
            mdi-account-group-outline
          </v-icon>
          <h3 class="text-h6 mb-2">Nenhum usuário encontrado</h3>
          <p class="text-body-2 text-medium-emphasis mb-4">
            {{ search ? 'Tente ajustar os filtros de busca' : 'Comece adicionando o primeiro usuário' }}
          </p>
          <v-btn v-if="!search" color="primary" prepend-icon="mdi-plus" @click="openDialog()">
            Adicionar Usuário
          </v-btn>
        </div>
      </template>
    </v-data-table>
  </v-card>

  <!-- Ações em Lote -->
  <v-slide-y-transition>
    <v-card v-if="selectedUsers.length > 0" class="mt-4" color="primary" variant="flat">
      <v-card-text class="d-flex align-center justify-space-between">
        <span class="text-white">
          {{ selectedUsers.length }} usuário(s) selecionado(s)
        </span>

        <div class="d-flex gap-2">
          <v-btn variant="outlined" color="white" prepend-icon="mdi-account-off" @click="bulkDeactivate">
            Desativar
          </v-btn>

          <v-btn variant="outlined" color="white" prepend-icon="mdi-delete" @click="bulkDelete">
            Excluir
          </v-btn>
        </div>
      </v-card-text>
    </v-card>
  </v-slide-y-transition>

  <!-- Dialog de Cadastro/Edição -->
  <v-dialog v-model="dialog" max-width="600">
    <v-card>
      <v-card-title class="d-flex align-center justify-space-between">
        <span class="text-h5">
          {{ editingUser ? 'Editar Usuário' : 'Novo Usuário' }}
        </span>
        <v-btn icon="mdi-close" variant="text" size="small" @click="closeDialog" />
      </v-card-title>

      <v-divider />

      <v-card-text class="pa-6">
        <v-form ref="formRef" v-model="formValid" @submit.prevent="saveUser">
          <v-row>
            <v-col cols="12">
              <v-text-field v-model="formData.nome" label="Nome Completo" :rules="nameRules" variant="outlined" required
                autofocus />
            </v-col>

            <v-col cols="12">
              <v-text-field v-model="formData.email" label="E-mail" :rules="emailRules" variant="outlined" type="email"
                required />
            </v-col>

            <v-col cols="12">
              <v-text-field v-model="formData.telefone" label="Telefone" :rules="phoneRules" variant="outlined"
                required />
            </v-col>

            <v-col cols="12">
              <v-text-field v-model="formData.usuario" label="Nome de Usuário" :rules="usernameRules" variant="outlined"
                required />
            </v-col>

            <v-col cols="12" v-if="!formData.administrador">
              <v-select v-model="formData.nivel" label="Nível de Acesso" :items="niveisAcesso" item-title="titulo"
                item-value="id" variant="outlined" required />
            </v-col>

            <v-col cols="12" sm="6">
              <v-text-field v-model="formData.senha" :label="editingUser ? 'Nova Senha (opcional)' : 'Senha'"
                :rules="passwordRules" variant="outlined" :type="showPassword ? 'text' : 'password'"
                :append-inner-icon="showPassword ? 'mdi-eye' : 'mdi-eye-off'"
                @click:append-inner="showPassword = !showPassword" />
            </v-col>

            <v-col cols="12" sm="6">
              <v-text-field v-model="formData.confirmarSenha" label="Confirmar Senha" :rules="confirmPasswordRules"
                variant="outlined" :type="showConfirmPassword ? 'text' : 'password'"
                :append-inner-icon="showConfirmPassword ? 'mdi-eye' : 'mdi-eye-off'"
                @click:append-inner="showConfirmPassword = !showConfirmPassword" />
            </v-col>

            <v-col cols="12" sm="6">
              <v-switch v-model="formData.administrador" label="Administrador" color="primary" hide-details />
            </v-col>

            <v-col cols="12" sm="6">
              <v-switch v-model="formData.ativo" label="Usuário Ativo" color="success" hide-details />
            </v-col>
          </v-row>
        </v-form>
      </v-card-text>

      <v-divider />

      <v-card-actions class="pa-4">
        <v-spacer />
        <v-btn variant="text" @click="closeDialog">
          Cancelar
        </v-btn>
        <v-btn color="primary" :loading="saving" :disabled="!formValid" @click="saveUser">
          {{ editingUser ? 'Salvar' : 'Criar' }}
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>

  <!-- Dialog de Confirmação de Exclusão -->
  <v-dialog v-model="deleteDialog" max-width="400">
    <v-card>
      <v-card-title class="text-h6">
        Confirmar Exclusão
      </v-card-title>

      <v-card-text>
        <p>Tem certeza que deseja excluir o usuário <strong>{{ userToDelete?.nome }}</strong>?</p>
        <p class="text-caption text-error mt-2">Esta ação não pode ser desfeita.</p>
      </v-card-text>

      <v-card-actions>
        <v-spacer />
        <v-btn variant="text" @click="deleteDialog = false">
          Cancelar
        </v-btn>
        <v-btn color="error" :loading="deleting" @click="deleteUser">
          Excluir
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>

  <!-- Snackbar para Notificações -->
  <v-snackbar v-model="snackbar.show" :color="snackbar.color" :timeout="3000" location="top right">
    {{ snackbar.message }}
    <template #actions>
      <v-btn variant="text" @click="snackbar.show = false">
        Fechar
      </v-btn>
    </template>
  </v-snackbar>
</template>

<script setup>
import { ref, computed, onMounted, nextTick } from 'vue'
import api from '@/services/api'

const loading = ref(false)
const saving = ref(false)
const deleting = ref(false)
const dialog = ref(false)
const deleteDialog = ref(false)
const formRef = ref(null)
const formValid = ref(false)
const showPassword = ref(false)
const showConfirmPassword = ref(false)

const users = ref([])
const selectedUsers = ref([])
const editingUser = ref(null)
const userToDelete = ref(null)

const search = ref('')
const statusFilter = ref(null)
const roleFilter = ref(null)

const formData = ref({
  nome: '',
  email: '',
  telefone: '',
  usuario: '',
  nivel: '0',
  senha: '',
  confirmarSenha: '',
  administrador: false,
  ativo: true
})

const niveisAcesso = ref([
  { id: '0', titulo: 'Comum' },
  { id: '1', titulo: 'Nível 3' }
])

const snackbar = ref({
  show: false,
  message: '',
  color: 'success'
})

const headers = [
  { title: '', key: 'avatar', sortable: false, width: '60px' },
  { title: 'Nome', key: 'nome', sortable: true },
  { title: 'Usuário', key: 'usuario', sortable: true },
  { title: 'Status', key: 'ativo', sortable: true, width: '100px' },
  { title: 'Perfil', key: 'administrador', sortable: true, width: '120px' },
  { title: 'Último Acesso', key: 'ultimo_acesso', sortable: true, width: '150px' },
  { title: 'Ações', key: 'actions', sortable: false, width: '100px' }
]

const statusOptions = [
  { title: 'Ativo', value: '1' },
  { title: 'Inativo', value: '0' }
]

const roleOptions = [
  { title: 'Administrador', value: '1' },
  { title: 'Usuário', value: '0' }
]

const filteredUsers = computed(() => {
  let filtered = [...users.value]

  if (statusFilter.value !== null) {
    filtered = filtered.filter(user => user.ativo === statusFilter.value)
  }

  if (roleFilter.value !== null) {
    filtered = filtered.filter(user => user.administrador === roleFilter.value)
  }

  return filtered
})

const nameRules = [
  v => !!v || 'Nome é obrigatório',
  v => (v && v.length >= 2) || 'Nome deve ter pelo menos 2 caracteres'
]

const emailRules = [
  v => !!v || 'E-mail é obrigatório',
  v => /.+@.+\..+/.test(v) || 'E-mail deve ser válido',
  v => (v && v.length <= 150) || 'E-mail deve ter no máximo 150 caracteres'
]

const phoneRules = [
  v => !!v || 'Telefone é obrigatório',
  v => (v && v.length >= 10) || 'Telefone deve ter pelo menos 10 dígitos',
  v => (v && v.length <= 15) || 'Telefone deve ter no máximo 15 dígitos',
  v => /^[0-9]+$/.test(v) || 'Telefone deve conter apenas números'
]

const usernameRules = [
  v => !!v || 'Nome de usuário é obrigatório',
  v => (v && v.length >= 3) || 'Nome de usuário deve ter pelo menos 3 caracteres',
  v => (v && v.length <= 50) || 'Nome de usuário deve ter no máximo 50 caracteres'
]

const passwordRules = computed(() => {
  if (editingUser.value) {

    return [
      v => !v || v.length >= 6 || 'Senha deve ter pelo menos 6 caracteres',
      v => !v || v.length <= 50 || 'Senha deve ter no máximo 50 caracteres'
    ]
  } else {

    return [
      v => !!v || 'Senha é obrigatória',
      v => (v && v.length >= 6) || 'Senha deve ter pelo menos 6 caracteres',
      v => (v && v.length <= 50) || 'Senha deve ter no máximo 50 caracteres'
    ]
  }
})

const confirmPasswordRules = computed(() => [
  v => {

    if (editingUser.value && !formData.value.senha) return true
    return v === formData.value.senha || 'Senhas não conferem'
  }
])

const showNotification = (message, color = 'success') => {
  snackbar.value = {
    show: true,
    message,
    color
  }
}

const clearFilters = () => {
  search.value = ''
  statusFilter.value = null
  roleFilter.value = null
}

const formatDate = (dateString) => {
  return dateString.split(' ')[0]
}

const formatTime = (dateString) => {
  if (!dateString) return ''
  const date = new Date(dateString)
  return date.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })
}

const resetForm = () => {
  formData.value = {
    nome: '',
    email: '',
    telefone: '',
    usuario: '',
    senha: '',
    confirmarSenha: '',
    administrador: false,
    ativo: true
  }

  if (formRef.value) {
    formRef.value.resetValidation()
  }
}

const openDialog = (user = null) => {
  editingUser.value = user

  if (user) {
    formData.value = {
      nome: user.nome,
      email: user.email || '',
      telefone: user.telefone || '',
      usuario: user.usuario,
      nivel: user.nivel,
      senha: '',
      confirmarSenha: '',
      administrador: user.administrador === '1',
      ativo: user.ativo === '1'
    }
  } else {
    resetForm()
  }

  dialog.value = true

  nextTick(() => {
    if (formRef.value) {
      formRef.value.resetValidation()
    }
  })
}

const closeDialog = () => {
  dialog.value = false
  editingUser.value = null
  resetForm()
}

const saveUser = async () => {
  if (!formRef.value) return

  const { valid } = await formRef.value.validate()
  if (!valid) return

  saving.value = true

  try {
    const userData = {
      nome: formData.value.nome,
      email: formData.value.email,
      telefone: formData.value.telefone,
      usuario: formData.value.usuario,
      nivel: formData.value.nivel,
      administrador: formData.value.administrador ? '1' : '0',
      ativo: formData.value.ativo ? '1' : '0'
    }

    if (formData.value.senha) {
      userData.senha = formData.value.senha
    }

    let response

    if (editingUser.value) {

      response = await api.put(`/usuarios/${editingUser.value.id}`, userData)

      if (response.data.status === 200) {

        const index = users.value.findIndex(u => u.id === editingUser.value.id)
        if (index !== -1) {
          users.value[index] = {
            ...users.value[index],
            ...userData
          }
        }
        showNotification('Usuário atualizado com sucesso!')
      } else {
        throw new Error(response.data.message || 'Erro ao atualizar usuário')
      }
    } else {

      response = await api.post('/usuarios', userData)

      if (response.data.status === 200 || response.data.status === 201) {

        await loadUsers()
        showNotification('Usuário criado com sucesso!')
      } else {
        throw new Error(response.data.message || 'Erro ao criar usuário')
      }
    }

    closeDialog()
  } catch (error) {
    console.error('Erro ao salvar usuário:', error)
    const errorMessage = error.response?.data?.message || error.message || 'Erro ao salvar usuário'
    showNotification(errorMessage, 'error')
  } finally {
    saving.value = false
  }
}

const confirmDelete = (user) => {
  userToDelete.value = user
  deleteDialog.value = true
}

const deleteUser = async () => {
  if (!userToDelete.value) return

  deleting.value = true

  try {
    const response = await api.delete(`/usuarios/${userToDelete.value.id}`)

    if (response.data.status === 200) {

      const index = users.value.findIndex(u => u.id === userToDelete.value.id)
      if (index !== -1) {
        users.value.splice(index, 1)
      }

      showNotification('Usuário excluído com sucesso!')
    } else {
      throw new Error(response.data.message || 'Erro ao excluir usuário')
    }

    deleteDialog.value = false
    userToDelete.value = null
  } catch (error) {
    console.error('Erro ao excluir usuário:', error)
    const errorMessage = error.response?.data?.message || error.message || 'Erro ao excluir usuário'
    showNotification(errorMessage, 'error')
  } finally {
    deleting.value = false
  }
}

const bulkDeactivate = async () => {
  try {
    selectedUsers.value.forEach(userId => {
      const user = users.value.find(u => u.id === userId)
      if (user) {
        user.ativo = '0'
      }
    })

    showNotification(`${selectedUsers.value.length} usuário(s) desativado(s)`)
    selectedUsers.value = []
  } catch (error) {
    console.error('Erro ao desativar usuários:', error)
    showNotification('Erro ao desativar usuários', 'error')
  }
}

const bulkDelete = async () => {
  if (!confirm(`Tem certeza que deseja excluir ${selectedUsers.value.length} usuário(s)?`)) {
    return
  }

  try {
    selectedUsers.value.forEach(userId => {
      const index = users.value.findIndex(u => u.id === userId)
      if (index !== -1) {
        users.value.splice(index, 1)
      }
    })

    showNotification(`${selectedUsers.value.length} usuário(s) excluído(s)`)
    selectedUsers.value = []
  } catch (error) {
    console.error('Erro ao excluir usuários:', error)
    showNotification('Erro ao excluir usuários', 'error')
  }
}

const loadUsers = async () => {
  loading.value = true

  try {
    const response = await api.get('/usuarios')

    if (response.data.status === 200 && response.data.data) {
      users.value = response.data.data.map(user => ({
        ...user,

        administrador: user.administrador || '0',
        ativo: user.ativo || '1',
        email: user.email || '',
        telefone: user.telefone || '',
        ultimo_acesso: user.ultimo_acesso || null
      }))

    } else {
      throw new Error(response.data.message || 'Erro ao carregar usuários')
    }
  } catch (error) {
    console.error('Erro ao carregar usuários:', error)
    showNotification('Erro ao carregar usuários: ' + (error.response?.data?.message || error.message), 'error')
    users.value = []
  } finally {
    loading.value = false
  }
}

onMounted(() => {
  loadUsers()
})
</script>

<style scoped>
.v-data-table {
  background-color: transparent;
}

.v-card {
  transition: all 0.3s ease;
}

/* Responsividade */
@media (max-width: 600px) {
  .pa-6 {
    padding: 1rem !important;
  }

  .text-h4 {
    font-size: 1.5rem !important;
  }
}
</style>
